(() => {
  const sectionId = 'furgo-cart-drawer';
  let active;

  const createDrawer = dialog => {
    if (typeof dialog.showModal !== 'function') return null;
    const content = dialog.querySelector('[data-cart-drawer-content]');
    const status = dialog.querySelector('[data-cart-drawer-status]');
    const errorPanel = dialog.querySelector('[data-cart-drawer-error-panel]');
    const errorText = dialog.querySelector('[data-cart-drawer-error]');
    const closeButton = dialog.querySelector('[data-cart-drawer-close]');
    const retryButton = dialog.querySelector('[data-cart-drawer-retry]');
    const lifecycle = new AbortController();
    const requests = new Set();
    let busy = false;
    let writing = false;
    let fresh = false;
    let generation = 0;
    let returnFocus;
    let unlock;
    let disposed = false;
    let queuedMessage;

    const controls = () => {
      content.querySelectorAll('[data-cart-control], [data-cart-drawer-checkout]').forEach(button => {
        button.disabled = busy || !fresh || button.dataset.limitDisabled === 'true';
      });
      retryButton.disabled = busy;
      dialog.setAttribute('aria-busy', String(busy));
    };

    const lockScroll = () => {
      if (unlock) return;
      const body = document.body;
      const html = document.documentElement;
      const x = window.scrollX;
      const y = window.scrollY;
      const width = window.innerWidth - html.clientWidth;
      const previous = {};
      ['position', 'top', 'left', 'width', 'overflow', 'paddingRight'].forEach(key => { previous[key] = body.style[key]; });
      const padding = getComputedStyle(body).paddingRight;
      body.style.position = 'fixed';
      body.style.top = `${-y}px`;
      body.style.left = `${-x}px`;
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      if (width > 0) body.style.paddingRight = `calc(${padding} + ${width}px)`;
      unlock = () => {
        const scrollBehavior = html.style.scrollBehavior;
        html.style.scrollBehavior = 'auto';
        Object.entries(previous).forEach(([key, value]) => { body.style[key] = value; });
        window.scrollTo({left:x, top:y, behavior:'instant'});
        html.style.scrollBehavior = scrollBehavior;
        unlock = null;
      };
    };

    const showError = message => {
      fresh = false;
      busy = false;
      status.textContent = '';
      errorPanel.hidden = false;
      errorText.textContent = message || dialog.dataset.error;
      content.hidden = false;
      controls();
    };

    const begin = (message, hideContent = false) => {
      busy = true;
      fresh = false;
      errorText.textContent = '';
      errorPanel.hidden = true;
      status.textContent = message;
      content.hidden = hideContent;
      controls();
    };

    const apply = (html, message, focusHint) => {
      if (typeof html !== 'string' || !html.trim()) return false;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incoming = parsed.querySelector('[data-cart-drawer-content]');
      const count = Number(incoming?.dataset.count);
      if (!incoming || !Number.isSafeInteger(count) || count < 0) return false;
      const oldScroll = content.querySelector('.furgo-cart-drawer__items')?.scrollTop || 0;
      const hadContentFocus = content.contains(document.activeElement);
      content.replaceChildren(...incoming.childNodes);
      content.dataset.count = String(count);
      content.hidden = false;
      const scrollable = content.querySelector('.furgo-cart-drawer__items');
      if (scrollable) scrollable.scrollTop = oldScroll;
      document.querySelectorAll('[data-cart-count]').forEach(counter => { counter.textContent = String(count); });
      fresh = true;
      busy = false;
      errorText.textContent = '';
      errorPanel.hidden = true;
      status.textContent = message;
      controls();
      if (dialog.open && hadContentFocus) {
        const next = focusHint && [...content.querySelectorAll('[data-cart-action]')].find(button =>
          button.dataset.cartKey === focusHint.key && button.dataset.cartAction === focusHint.action && !button.disabled);
        (next || closeButton).focus({preventScroll:true});
      }
      document.dispatchEvent(new CustomEvent('furgo:cart:updated', {detail:{itemCount:count}}));
      return true;
    };

    const request = async (url, options = {}) => {
      const controller = new AbortController();
      requests.add(controller);
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, {...options, credentials:'same-origin', signal:controller.signal, headers:{Accept:'application/json', ...options.headers}});
        const data = await response.json();
        if (!response.ok) {
          const error = new Error('Cart request failed');
          if (typeof data.description === 'string') error.safeMessage = `${data.description} ${dialog.dataset.error}`;
          throw error;
        }
        return data;
      } finally {
        clearTimeout(timer);
        requests.delete(controller);
      }
    };

    const read = async (ticket, message, focusHint) => {
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id');
      url.searchParams.set('sections', sectionId);
      const data = await request(url.pathname + url.search);
      if (ticket !== generation || disposed) return;
      if (!apply(data[sectionId], message, focusHint)) throw new Error('Cart section missing');
    };

    const refresh = (message = dialog.dataset.ready) => {
      const ticket = ++generation;
      requests.forEach(controller => controller.abort());
      begin(dialog.dataset.loading, true);
      read(ticket, message).catch(error => {
        if (ticket === generation && !disposed) showError(error.safeMessage);
      });
    };

    const change = async button => {
      if (busy || !fresh || button.disabled) return;
      const key = button.dataset.cartKey;
      const quantity = button.hasAttribute('data-cart-remove') ? 0 : Number(button.dataset.cartQuantity);
      if (!key || !Number.isSafeInteger(quantity) || quantity < 0) return;
      const focusHint = {key, action:button.dataset.cartAction};
      const ticket = ++generation;
      const base = window.Shopify?.routes?.root || '/';
      writing = true;
      begin(dialog.dataset.updating);
      try {
        const data = await request(base + 'cart/change.js', {
          method:'POST', headers:{'Content-Type':'application/json'},
          body:JSON.stringify({id:key, quantity, sections:sectionId, sections_url:window.location.pathname})
        });
        if (ticket !== generation || disposed) return;
        if (!apply(data.sections?.[sectionId], dialog.dataset.updated, focusHint)) {
          await read(ticket, dialog.dataset.updated, focusHint);
        }
      } catch (error) {
        if (ticket === generation && !disposed) showError(error.safeMessage);
      } finally {
        writing = false;
        if (queuedMessage && !disposed) {
          const message = queuedMessage;
          queuedMessage = null;
          refresh(message);
        }
      }
    };

    const open = ({trigger, html, added = false} = {}) => {
      if (disposed) return false;
      if (!dialog.open) {
        returnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
        try { dialog.showModal(); } catch { return false; }
        lockScroll();
        closeButton.focus({preventScroll:true});
      }
      if (writing) {
        queuedMessage = added ? dialog.dataset.added : (queuedMessage || dialog.dataset.ready);
        return true;
      }
      const message = added ? dialog.dataset.added : dialog.dataset.ready;
      ++generation;
      requests.forEach(controller => controller.abort());
      if (!apply(html, message)) refresh(message);
      return true;
    };

    dialog.addEventListener('click', event => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('[data-cart-drawer-close]')) { dialog.close(); return; }
      if (target === dialog) {
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
        return;
      }
      if (target.closest('[data-cart-drawer-retry]')) { if (!busy) refresh(); return; }
      const button = target.closest('[data-cart-quantity], [data-cart-remove]');
      if (button) void change(button);
    }, {signal:lifecycle.signal});

    dialog.addEventListener('submit', event => {
      if (event.target.matches('[data-cart-drawer-form]') && (busy || !fresh)) event.preventDefault();
    }, {signal:lifecycle.signal});

    dialog.addEventListener('close', () => {
      unlock?.();
      const target = returnFocus?.isConnected && !returnFocus.disabled ? returnFocus : document.querySelector('[data-furgo-cart-open]');
      target?.focus({preventScroll:true});
    }, {signal:lifecycle.signal});

    document.addEventListener('click', event => {
      const target = event.target instanceof Element ? event.target.closest('[data-furgo-cart-open]') : null;
      if (!target || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (open({trigger:target})) event.preventDefault();
    }, {signal:lifecycle.signal});

    const destroy = () => {
      disposed = true;
      ++generation;
      lifecycle.abort();
      requests.forEach(controller => controller.abort());
      if (dialog.open) dialog.close();
      unlock?.();
      if (window.FurgoCart === api) delete window.FurgoCart;
    };
    const api = {open};
    window.FurgoCart = api;
    return {dialog, destroy};
  };

  const init = () => {
    const dialog = document.getElementById('FurgoCartDrawer');
    if (active?.dialog === dialog) return;
    active?.destroy();
    active = dialog ? createDrawer(dialog) : null;
  };
  init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => {
    if (active && event.target.contains(active.dialog)) { active.destroy(); active = null; }
  });
})();
